import React from "react";
import { Route, Switch } from "react-router-dom";
import { Alert, Snackbar, Box, Slide } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { GlobalContext } from "./context/GlobalContext";
import PublicRoute from "./routing/PublicRoute";
import AdminRoute from "./routing/AdminRoute";
import UserRoute from "./routing/UserRoute";
import { TranslateContext } from "./context/TranslateContext";
import axios from "axios";
import FrontEnd from "./frontend/FrontEnd";
import AdminLogin from "./admin/login/AdminLogin";
import LoadingComp from "./LoadingComp";
import AdminDashboard from "./admin/dashboard/AdminDashboard";
import UserLogin from "./user/login/UserLogin";
import UserRegister from "./user/register/UserRegister";
import UserDashboard from "./user/dashboard/UserDashboard";
import ViewPage from "./frontend/page/ViewPage";
import ForgotPassword from "./user/pages/ForgotPassword";
import AdminForgotPassword from "./admin/login/AdminForgotPassword";
import Error404 from "./frontend/Error404";

function SlideTransition(props) {
  return <Slide {...props} direction="down" />;
}

const HomeMainPage = () => {
  const GLOBAL_CONTEXT = React.useContext(GlobalContext);
  const { web } = React.useContext(GlobalContext);
  const LANGUAGE = React.useContext(TranslateContext);

  // ── Derived meta values with safe fallbacks ──────────────────────────────
  const siteName = web?.site_name || "TorrentCloud";
  const metaTitle = web?.meta_title || siteName;
  const metaDesc = web?.meta_description || "";
  const metaKw = web?.meta_keywords || "";
  const ogTitle = web?.og_title || metaTitle;
  const ogDesc = web?.og_description || metaDesc;
  const ogImage = web?.og_image
    ? `${process.env.REACT_APP_BASE_URL}/media/${web.og_image}`
    : "";
  const faviconFile = web?.site_favicon || web?.site_logo || "";
  const faviconUrl = faviconFile ? `/media/${faviconFile}` : "/favicon.ico";
  const siteUrl = window.location.origin;

  React.useEffect(() => {
    const code = localStorage.getItem("language");
    if (code) {
      axios
        .get(
          `${process.env.REACT_APP_BASE_URL}/api/web/get-one-translation?code=${code}`,
        )
        .then((res) => {
          if (res.data.notfound) {
            localStorage.removeItem("language");
            return;
          }
          LANGUAGE.setData({ ...res.data.data });
        })
        .catch(console.log);
    } else {
      axios
        .get(
          `${process.env.REACT_APP_BASE_URL}/api/web/get-all-translation-name`,
        )
        .then((lan) => {
          const code = lan.data.data[0]?.replace(".json", "");
          localStorage.setItem("language", code);
          axios
            .get(
              `${process.env.REACT_APP_BASE_URL}/api/web/get-one-translation?code=${code}`,
            )
            .then((res) => LANGUAGE.setData({ ...res.data.data }))
            .catch(console.log);
        });
    }
  }, []);

  return (
    <Box
      sx={{ position: "relative" }}
      color="text.primary"
      minHeight="100vh"
      bgcolor="background.default"
    >
      {/* ── Global Head / SEO / OG Tags ──────────────────────────────────── */}
      <Helmet>
        {/* ── Browser Tab ── */}
        <title>{metaTitle}</title>
        <link rel="icon" type="image/x-icon" href={faviconUrl} />
        <link rel="shortcut icon" href={faviconUrl} />
        <link rel="apple-touch-icon" href={faviconUrl} />

        {/* ── Basic SEO ── */}
        {metaDesc && <meta name="description" content={metaDesc} />}
        {metaKw && <meta name="keywords" content={metaKw} />}
        <meta name="robots" content="index, follow" />
        <meta name="author" content={siteName} />

        {/* ── Canonical ── */}
        <link rel="canonical" href={siteUrl} />

        {/* ── Open Graph (Facebook / LinkedIn / WhatsApp) ── */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={siteName} />
        <meta property="og:url" content={siteUrl} />
        <meta property="og:title" content={ogTitle} />
        {ogDesc && <meta property="og:description" content={ogDesc} />}
        {ogImage && <meta property="og:image" content={ogImage} />}
        {ogImage && <meta property="og:image:width" content="1200" />}
        {ogImage && <meta property="og:image:height" content="630" />}

        {/* ── Twitter Card ── */}
        <meta
          name="twitter:card"
          content={ogImage ? "summary_large_image" : "summary"}
        />
        <meta name="twitter:title" content={ogTitle} />
        {ogDesc && <meta name="twitter:description" content={ogDesc} />}
        {ogImage && <meta name="twitter:image" content={ogImage} />}

        {/* ── Google Analytics ── */}
        {web?.google_analytics_id && (
          <script
            async
            src={`https://www.googletagmanager.com/gtag/js?id=${web.google_analytics_id}`}
          />
        )}
        {web?.google_analytics_id && (
          <script>{`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${web.google_analytics_id}');
          `}</script>
        )}

        {/* ── Google Tag Manager ── */}
        {web?.google_tag_manager_id && (
          <script>{`
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${web.google_tag_manager_id}');
          `}</script>
        )}

        {/* ── Facebook Pixel ── */}
        {web?.facebook_pixel_id && (
          <script>{`
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${web.facebook_pixel_id}');
            fbq('track', 'PageView');
          `}</script>
        )}
      </Helmet>

      {/* ── GTM noscript fallback (must be in <body>) ─────────────────────── */}
      {web?.google_tag_manager_id && (
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${web.google_tag_manager_id}`}
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
            title="gtm"
          />
        </noscript>
      )}

      {GLOBAL_CONTEXT.data.loading && <LoadingComp />}

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={GLOBAL_CONTEXT.data.snack}
        autoHideDuration={4000}
        TransitionComponent={SlideTransition}
        sx={{ top: { xs: 16, sm: 24 } }}
      >
        <Alert
          severity={GLOBAL_CONTEXT.data.snack_success ? "success" : "error"}
          variant="filled"
          sx={{
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            fontWeight: 600,
            "& .MuiAlert-icon": { fontSize: 20 },
          }}
        >
          {GLOBAL_CONTEXT.data.snack_msg}
        </Alert>
      </Snackbar>

      <Box
        sx={{
          opacity: GLOBAL_CONTEXT.data.loading ? 0.4 : 1,
          pointerEvents: GLOBAL_CONTEXT.data.loading ? "none" : "initial",
          transition: "opacity 0.2s ease",
        }}
      >
        <Switch>
          <PublicRoute path="/" exact component={FrontEnd} />
          <PublicRoute path="/admin/login" exact component={AdminLogin} />
          <AdminRoute path="/admin" exact component={AdminDashboard} />
          <PublicRoute path="/user/login" exact component={UserLogin} />
          <PublicRoute path="/user/register" exact component={UserRegister} />
          <UserRoute path="/user" exact component={UserDashboard} />
          <PublicRoute path="/view/:slug" exact component={ViewPage} />
          <PublicRoute
            path="/user/forgot-password"
            exact
            component={ForgotPassword}
          />
          <PublicRoute
            path="/admin/forgot-password"
            exact
            component={AdminForgotPassword}
          />

          <PublicRoute path="*" component={Error404} />
        </Switch>
      </Box>
    </Box>
  );
};

export default HomeMainPage;
